/**
 * What a body's albedo bytes mean: sRGB-encoded colour, or an 8-bit linear
 * stretch of a greyscale reflectance product.
 *
 * A row's albedo product declares `albedo_transfer` when its DN is linear in
 * reflectance (the LROC WAC and New Horizons mosaics; the row's `source` says
 * how each was established), and the ground's linear reflectance is then
 * `reflectance_per_dn * (DN - dn_zero)`. Every other body's imagery is colour
 * and keeps the sRGB decode. The tiles, the globe map and the plot's colour all
 * read the same bytes, so all three decode them here.
 */
import * as THREE from 'three';

import type { BodyRow } from '../world/types.ts';
import { TEST_SWITCHES, devFlag, urlFlag } from '../urlFlags.ts';

/** `ground-albedo-check --inject srgb`: every body's bytes read as sRGB colour again. */
const srgbEverywhere = devFlag(() => TEST_SWITCHES && urlFlag('albedosrgb'));

/** `ground-albedo-check --inject fill-raw`: the hole fills read as sRGB colour again. */
const fillRaw = devFlag(() => TEST_SWITCHES && urlFlag('albedofillraw'));

export interface AlbedoTransfer {
  readonly dnZero: number;
  readonly perDn: number;
}

export function albedoTransferOf(row: BodyRow | null | undefined): AlbedoTransfer | null {
  const t = row?.bake?.global.find((g) => g.role === 'albedo')?.albedo_transfer;
  return t && !srgbEverywhere() ? { dnZero: t.dn_zero, perDn: t.reflectance_per_dn } : null;
}

export function albedoFillTransferOf(t: AlbedoTransfer | null): AlbedoTransfer | null {
  return fillRaw() ? null : t;
}

function srgbToLinear(byte: number): number {
  const c = byte / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function linearToSrgbByte(linear: number): number {
  const c = Math.min(1, Math.max(0, linear));
  return 255 * (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);
}

/** DN to linear reflectance, one entry per byte value. */
export function albedoByteTable(t: AlbedoTransfer | null): Float32Array {
  return Float32Array.from({ length: 256 }, (_, i) => (t
    ? Math.min(1, Math.max(0, t.perDn * (i - t.dnZero)))
    : srgbToLinear(i)));
}

/** A linear product is uploaded undecoded so the shader sees the DN itself. */
export function albedoColorSpace(t: AlbedoTransfer | null): THREE.ColorSpace {
  return t ? THREE.NoColorSpace : THREE.SRGBColorSpace;
}

/** x the DN at zero reflectance, y reflectance per DN, z 1 while the drawn map is the body's own. */
export type AlbedoTransferUniform = { value: THREE.Vector3 };

export function albedoTransferUniform(t: AlbedoTransfer | null, on: boolean): AlbedoTransferUniform {
  return { value: new THREE.Vector3(t?.dnZero ?? 0, t?.perDn ?? 0, t && on ? 1 : 0) };
}

/**
 * Straight after the sampled texel, so it has to be the LAST replace of
 * `map_fragment` a hook makes: every later one splices in ahead of it.
 */
export function applyAlbedoTransfer(shader: THREE.WebGLProgramParametersWithUniforms,
  u: AlbedoTransferUniform): void {
  shader.uniforms['fxAlbedoDn'] = u;
  shader.fragmentShader = `uniform vec3 fxAlbedoDn;\n${shader.fragmentShader}`
    .replace('#include <map_fragment>', '#include <map_fragment>\n'
      + '  if (fxAlbedoDn.z > 0.5) diffuseColor.rgb = clamp(\n'
      + '    fxAlbedoDn.y * (diffuseColor.rgb * 255.0 - fxAlbedoDn.x), 0.0, 1.0);');
}

const ALBEDO_TRANSFER_REF = 'fxAlbedoTransfer';

export function attachAlbedoTransfer(mat: THREE.Material, u: AlbedoTransferUniform): void {
  mat.userData[ALBEDO_TRANSFER_REF] = u;
}

export function albedoTransferRefOf(mat: THREE.Material): AlbedoTransferUniform | null {
  return (mat.userData[ALBEDO_TRANSFER_REF] as AlbedoTransferUniform | undefined) ?? null;
}
